import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

const ONBOARDING_COMPLETE_KEY = "eloplus_onboarding_complete";

export function readOnboardingComplete(): boolean {
  try {
    return localStorage.getItem(ONBOARDING_COMPLETE_KEY) === "true";
  } catch {
    return false;
  }
}

export function writeOnboardingComplete(): void {
  try {
    localStorage.setItem(ONBOARDING_COMPLETE_KEY, "true");
  } catch {
    /* ignore */
  }
}

type MarketRegion = "ERCOT" | "PJM" | "CAISO";
type JobPriority = "CRITICAL" | "FLEXIBLE";
type InstallTab = "curl" | "manual" | "docker";
type SchedulerChoice = "kubernetes" | "ray" | "slurm" | "shell";
type VerifyPhase = "waiting" | "detected" | "connected";

interface Job {
  id: string;
  name: string;
  priority: JobPriority;
  power_kw: number;
  status: string;
  created_at: string;
}

interface AgentDetail {
  token_exists: boolean;
  is_online: boolean;
  last_seen: string | null;
  total_events: number;
}

interface OnboardingWizardProps {
  open: boolean;
  apiBase: string;
  authHeader: Record<string, string>;
  marketRegion: MarketRegion;
  priceThreshold: number;
  setPriceThreshold: (value: number) => void;
  jobs: Job[];
  effectiveJobPriorities: Record<string, JobPriority>;
  currentPriceMwh: number | null;
  onSaveSettings: () => Promise<void>;
  onUpdateJobPriority: (jobId: string, priority: JobPriority) => void;
  onRefreshJobs: () => Promise<void>;
  fetchAgentDetail: () => Promise<AgentDetail | null>;
  onComplete: () => void;
}

const REGION_AVG_PRICE: Record<MarketRegion, number> = {
  ERCOT: 47,
  PJM: 42,
  CAISO: 55,
};

const SCHEDULERS: SchedulerChoice[] = ["kubernetes", "ray", "slurm", "shell"];

function ConfettiBurst({ active }: { active: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!active || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const colors = ["#0EA5A0", "#22C55E", "#FACC15", "#67E8F9", "#A78BFA"];
    const pieces = Array.from({ length: 120 }, () => ({
      x: Math.random() * canvas.width,
      y: -20 - Math.random() * canvas.height * 0.3,
      r: 4 + Math.random() * 6,
      vx: -2 + Math.random() * 4,
      vy: 2 + Math.random() * 5,
      color: colors[Math.floor(Math.random() * colors.length)],
      rot: Math.random() * Math.PI,
      vr: -0.15 + Math.random() * 0.3,
    }));

    let frame = 0;
    let raf = 0;
    const draw = () => {
      frame += 1;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of pieces) {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.06;
        p.rot += p.vr;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.r / 2, -p.r / 2, p.r, p.r * 0.6);
        ctx.restore();
      }
      if (frame < 150) raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, [active]);

  if (!active) return null;
  return (
    <canvas
      ref={canvasRef}
      style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 80 }}
      aria-hidden
    />
  );
}


// Avoid importing motion - use div only
function StepDots({ step }: { step: number }) {
  return (
    <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 8 }}>
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: i < step ? "#0EA5A0" : i === step ? "#0F766E" : "#E2E8F0",
            boxShadow: i === step ? "0 0 0 3px rgba(14, 165, 160, 0.25)" : "none",
            transition: "all 200ms ease",
          }}
        />
      ))}
    </div>
  );
}

export default function OnboardingWizard({
  open,
  apiBase,
  authHeader,
  marketRegion,
  priceThreshold,
  setPriceThreshold,
  jobs,
  effectiveJobPriorities,
  currentPriceMwh,
  onSaveSettings,
  onUpdateJobPriority,
  onRefreshJobs,
  fetchAgentDetail,
  onComplete,
}: OnboardingWizardProps) {
  const [step, setStep] = useState(0);
  const [fadeKey, setFadeKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [agentToken, setAgentToken] = useState<string | null>(null);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [generatingToken, setGeneratingToken] = useState(false);

  const [installTab, setInstallTab] = useState<InstallTab>("curl");
  const [scheduler, setScheduler] = useState<SchedulerChoice>("kubernetes");

  const [verifyPhase, setVerifyPhase] = useState<VerifyPhase>("waiting");
  const [verifyTimedOut, setVerifyTimedOut] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const [newJobName, setNewJobName] = useState("");
  const [newJobType, setNewJobType] = useState("training");
  const [addingJob, setAddingJob] = useState(false);

  const pollRef = useRef<number | null>(null);
  const verifyStartRef = useRef<number>(0);

  const goToStep = (next: number) => {
    setFadeKey((k) => k + 1);
    setStep(next);
    setError(null);
  };

  const flexibleJobs = useMemo(
    () =>
      jobs.filter((j) => (effectiveJobPriorities[j.id] ?? j.priority) === "FLEXIBLE"),
    [jobs, effectiveJobPriorities]
  );

  const criticalJobs = useMemo(
    () =>
      jobs.filter((j) => (effectiveJobPriorities[j.id] ?? j.priority) === "CRITICAL"),
    [jobs, effectiveJobPriorities]
  );

  const regionAvg = REGION_AVG_PRICE[marketRegion] ?? 47;

  const installCommands = useMemo(() => {
    const token = agentToken ?? "YOUR_TOKEN";
    const curlCmd = `curl -sSL https://eloplus.ai/install.sh | bash -s -- --token ${token} --scheduler ${scheduler} --mode automatic`;
    const manualCmd = [
      "pip install eloplus-agent",
      `eloplus init --token ${token} --scheduler ${scheduler} --mode automatic --non-interactive`,
      "eloplus start",
    ].join("\n");
    const dockerCmd = `docker run -d \\
  -e ELOPLUS_TOKEN=${token} \\
  -e ELOPLUS_SCHEDULER=${scheduler} \\
  -e ELOPLUS_MODE=automatic \\
  --name eloplus-agent \\
  eloplus/agent:latest`;
    return { curlCmd, manualCmd, dockerCmd };
  }, [agentToken, scheduler]);

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* ignore */
    }
  };

  const generateToken = async () => {
    setGeneratingToken(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/api/agent/token`, {
        method: "POST",
        headers: { ...authHeader, "Content-Type": "application/json" },
        body: JSON.stringify({ name: "primary" }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { detail?: string };
        throw new Error(typeof data.detail === "string" ? data.detail : "Could not generate token.");
      }
      const data = (await res.json()) as { token: string };
      setAgentToken(data.token);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Token generation failed.");
    } finally {
      setGeneratingToken(false);
    }
  };

  const addFlexibleJob = async () => {
    if (!newJobName.trim()) {
      setError("Enter a job name.");
      return;
    }
    setAddingJob(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/api/jobs`, {
        method: "POST",
        headers: { ...authHeader, "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newJobName.trim(),
          job_type: newJobType,
          priority: "FLEXIBLE",
        }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { detail?: string };
        throw new Error(typeof data.detail === "string" ? data.detail : "Could not add job.");
      }
      setNewJobName("");
      await onRefreshJobs();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add job.");
    } finally {
      setAddingJob(false);
    }
  };

  const finishOnboarding = useCallback(() => {
    writeOnboardingComplete();
    setShowConfetti(true);
    setTimeout(() => {
      setShowConfetti(false);
      onComplete();
    }, 2200);
  }, [onComplete]);

  const evaluateAgent = useCallback(
    (detail: AgentDetail | null) => {
      if (!detail?.token_exists) {
        setVerifyPhase("waiting");
        return;
      }
      if (detail.is_online) {
        setVerifyPhase("connected");
        return;
      }
      if (detail.last_seen || (detail.total_events ?? 0) > 0) {
        setVerifyPhase("detected");
        return;
      }
      setVerifyPhase("waiting");
    },
    []
  );

  useEffect(() => {
    if (!open || step !== 4) {
      if (pollRef.current != null) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }

    setVerifyPhase("waiting");
    setVerifyTimedOut(false);
    verifyStartRef.current = Date.now();

    const poll = async () => {
      const detail = await fetchAgentDetail();
      evaluateAgent(detail);
      if (detail?.is_online) {
        if (pollRef.current != null) {
          window.clearInterval(pollRef.current);
          pollRef.current = null;
        }
        setTimeout(() => finishOnboarding(), 2000);
      }
      if (Date.now() - verifyStartRef.current > 180_000) {
        setVerifyTimedOut(true);
      }
    };

    void poll();
    pollRef.current = window.setInterval(() => void poll(), 3000);

    return () => {
      if (pollRef.current != null) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [open, step, fetchAgentDetail, evaluateAgent, finishOnboarding]);

  useEffect(() => {
    if (open) {
      setStep(0);
      setError(null);
      setAgentToken(null);
      setTokenCopied(false);
      setVerifyTimedOut(false);
      setShowConfetti(false);
    }
  }, [open]);

  if (!open) return null;

  const cardStyle: React.CSSProperties = {
    width: "min(640px, 100%)",
    maxHeight: "min(90vh, 820px)",
    overflowY: "auto",
    background: "#FFFFFF",
    color: "#0F172A",
    borderRadius: 16,
    padding: "28px 28px 24px",
    boxShadow: "0 24px 64px rgba(0, 0, 0, 0.35)",
    display: "grid",
    gap: 20,
  };

  const primaryBtn: React.CSSProperties = {
    background: "#0EA5A0",
    color: "#FFFFFF",
    border: "none",
    borderRadius: 10,
    padding: "12px 20px",
    fontWeight: 700,
    fontSize: 15,
    cursor: "pointer",
    width: "100%",
  };

  const secondaryBtn: React.CSSProperties = {
    background: "transparent",
    color: "#64748B",
    border: "none",
    cursor: "pointer",
    fontSize: 14,
    padding: "8px 0",
  };

  const codeBlock: React.CSSProperties = {
    background: "#F1F5F9",
    border: "1px solid #E2E8F0",
    borderRadius: 8,
    padding: 12,
    fontSize: 12,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    whiteSpace: "pre-wrap",
    wordBreak: "break-all",
    color: "#0F172A",
  };

  return (
    <>
      <ConfettiBurst active={showConfetti} />
      <div
        role="dialog"
        aria-modal
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 70,
          background: "rgba(15, 23, 42, 0.72)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
        }}
      >
        <div style={cardStyle} key={fadeKey}>
          <div>
            <StepDots step={step} />
            <div style={{ textAlign: "center", fontSize: 12, color: "#64748B", fontWeight: 600 }}>
              Step {step + 1} of 5
            </div>
          </div>

          <div style={{ animation: "fadeIn 280ms ease" }}>
            {step === 0 && (
              <>
                <h1 style={{ margin: "0 0 12px", fontSize: 26, fontWeight: 800, color: "#0F172A" }}>
                  Welcome to EloPlus
                </h1>
                <p style={{ margin: "0 0 20px", fontSize: 15, lineHeight: 1.6, color: "#475569" }}>
                  You&apos;re about to connect your infrastructure to the power grid. When prices spike, we
                  automatically pause your flexible AI training jobs and resume them when prices normalize.
                  Setup takes under 10 minutes.
                </p>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                    gap: 12,
                    marginBottom: 8,
                  }}
                >
                  {[
                    { title: "20–30%", sub: "reduction in power costs" },
                    { title: "< 3 sec", sub: "response time" },
                    { title: "Zero", sub: "manual intervention" },
                  ].map((item) => (
                    <div
                      key={item.sub}
                      style={{
                        border: "1px solid #E2E8F0",
                        borderRadius: 12,
                        padding: 14,
                        textAlign: "center",
                        background: "#F8FAFC",
                      }}
                    >
                      <div style={{ fontSize: 20, fontWeight: 800, color: "#0F766E" }}>{item.title}</div>
                      <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>{item.sub}</div>
                    </div>
                  ))}
                </div>
                <button type="button" style={primaryBtn} onClick={() => goToStep(1)}>
                  Let&apos;s get started →
                </button>
              </>
            )}

            {step === 1 && (
              <>
                <h2 style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 800 }}>Set your spike threshold</h2>
                <p style={{ margin: "0 0 16px", fontSize: 14, lineHeight: 1.55, color: "#475569" }}>
                  Region: <strong>{marketRegion}</strong>. When the price goes above{" "}
                  <strong>${priceThreshold}/MWh</strong>, we pause your flexible jobs. {marketRegion} averages
                  ~${regionAvg}/MWh — spikes can hit $500–$9,000/MWh.
                </p>
                <label style={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>
                  Spike threshold: ${priceThreshold}/MWh
                </label>
                <input
                  type="range"
                  min={50}
                  max={500}
                  value={priceThreshold}
                  onChange={(e) => setPriceThreshold(Number(e.target.value))}
                  style={{ width: "100%", marginTop: 8, accentColor: "#0EA5A0" }}
                />

                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 8 }}>
                    Your workloads
                  </div>
                  {jobs.length > 0 ? (
                    <div style={{ display: "grid", gap: 8, maxHeight: 160, overflowY: "auto" }}>
                      {jobs.map((job) => {
                        const pri = effectiveJobPriorities[job.id] ?? job.priority;
                        const isCrit = pri === "CRITICAL";
                        return (
                          <div
                            key={job.id}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "8px 10px",
                              borderRadius: 8,
                              background: isCrit ? "#FEF2F2" : "#F0FDFA",
                              border: `1px solid ${isCrit ? "#FECACA" : "#99F6E4"}`,
                              fontSize: 13,
                            }}
                          >
                            <span style={{ fontWeight: 600 }}>{job.name}</span>
                            <select
                              value={pri}
                              onChange={(e) =>
                                onUpdateJobPriority(job.id, e.target.value as JobPriority)
                              }
                              style={{
                                fontSize: 12,
                                borderRadius: 6,
                                border: "1px solid #CBD5E1",
                                padding: "4px 6px",
                              }}
                            >
                              <option value="CRITICAL">CRITICAL — never paused</option>
                              <option value="FLEXIBLE">FLEXIBLE — paused on spikes</option>
                            </select>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                  {flexibleJobs.length === 0 && (
                    <div
                      style={{
                        marginTop: 10,
                        padding: 12,
                        borderRadius: 10,
                        background: "#FFF7ED",
                        border: "1px solid #FDBA74",
                      }}
                    >
                      <p style={{ margin: "0 0 10px", fontSize: 13, color: "#9A3412" }}>
                        Add at least one <strong>FLEXIBLE</strong> job we can pause during spikes.
                      </p>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <input
                          type="text"
                          placeholder="Job name"
                          value={newJobName}
                          onChange={(e) => setNewJobName(e.target.value)}
                          style={{
                            flex: 1,
                            minWidth: 120,
                            padding: "8px 10px",
                            borderRadius: 8,
                            border: "1px solid #CBD5E1",
                          }}
                        />
                        <select
                          value={newJobType}
                          onChange={(e) => setNewJobType(e.target.value)}
                          style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #CBD5E1" }}
                        >
                          <option value="training">Training</option>
                          <option value="inference">Inference</option>
                          <option value="batch">Batch</option>
                        </select>
                        <button
                          type="button"
                          disabled={addingJob}
                          onClick={() => void addFlexibleJob()}
                          style={{
                            ...primaryBtn,
                            width: "auto",
                            padding: "8px 14px",
                          }}
                        >
                          {addingJob ? "Adding…" : "Add job"}
                        </button>
                      </div>
                    </div>
                  )}
                  {criticalJobs.length > 0 && flexibleJobs.length > 0 && (
                    <p style={{ margin: "10px 0 0", fontSize: 12, color: "#64748B" }}>
                      {criticalJobs.length} critical (protected) · {flexibleJobs.length} flexible (pausable)
                    </p>
                  )}
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                  <button type="button" style={secondaryBtn} onClick={() => goToStep(0)}>
                    ← Back
                  </button>
                  <button
                    type="button"
                    style={{ ...primaryBtn, width: "auto", minWidth: 180 }}
                    disabled={saving || flexibleJobs.length === 0}
                    onClick={async () => {
                      setSaving(true);
                      setError(null);
                      try {
                        await onSaveSettings();
                        goToStep(2);
                      } catch (err) {
                        setError(err instanceof Error ? err.message : "Could not save settings.");
                      } finally {
                        setSaving(false);
                      }
                    }}
                  >
                    {saving ? "Saving…" : "Save & Continue →"}
                  </button>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <h2 style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 800 }}>Create your connection token</h2>
                <p style={{ margin: "0 0 16px", fontSize: 14, lineHeight: 1.55, color: "#475569" }}>
                  This token lets the EloPlus agent on your servers talk to us securely. It&apos;s generated once
                  and stored on your infrastructure.
                </p>
                {!agentToken ? (
                  <button
                    type="button"
                    style={primaryBtn}
                    disabled={generatingToken}
                    onClick={() => void generateToken()}
                  >
                    {generatingToken ? "Generating…" : "Generate Token"}
                  </button>
                ) : (
                  <>
                    <div style={codeBlock}>{agentToken}</div>
                    <button
                      type="button"
                      style={{
                        ...primaryBtn,
                        background: "#F1F5F9",
                        color: "#0F172A",
                        border: "1px solid #CBD5E1",
                        marginTop: 8,
                      }}
                      onClick={async () => {
                        await copyText(agentToken);
                        setTokenCopied(true);
                      }}
                    >
                      {tokenCopied ? "Copied ✓" : "Copy"}
                    </button>
                    <p style={{ margin: "12px 0 0", fontSize: 13, color: "#B45309", fontWeight: 600 }}>
                      Save this token now — we don&apos;t store it and can&apos;t show it again.
                    </p>
                    <button
                      type="button"
                      style={{ ...primaryBtn, marginTop: 16 }}
                      onClick={() => goToStep(3)}
                    >
                      I&apos;ve copied my token →
                    </button>
                  </>
                )}
                <button type="button" style={{ ...secondaryBtn, marginTop: 8 }} onClick={() => goToStep(1)}>
                  ← Back
                </button>
              </>
            )}

            {step === 3 && (
              <>
                <h2 style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 800 }}>Install on your infrastructure</h2>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                  {(
                    [
                      ["curl", "One-line install"],
                      ["manual", "Manual install"],
                      ["docker", "Docker"],
                    ] as const
                  ).map(([tab, label]) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setInstallTab(tab)}
                      style={{
                        padding: "8px 12px",
                        borderRadius: 8,
                        border: installTab === tab ? "2px solid #0EA5A0" : "1px solid #E2E8F0",
                        background: installTab === tab ? "#F0FDFA" : "#FFF",
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: "pointer",
                        opacity: tab === "docker" ? 0.55 : 1,
                      }}
                    >
                      {label}
                      {tab === "docker" ? " (soon)" : ""}
                    </button>
                  ))}
                </div>

                {installTab === "curl" && (
                  <div>
                    <div style={codeBlock}>{installCommands.curlCmd}</div>
                    <button
                      type="button"
                      style={{ ...secondaryBtn, marginTop: 6 }}
                      onClick={() => void copyText(installCommands.curlCmd)}
                    >
                      Copy command
                    </button>
                  </div>
                )}
                {installTab === "manual" && (
                  <div>
                    <div style={codeBlock}>{installCommands.manualCmd}</div>
                    <button
                      type="button"
                      style={{ ...secondaryBtn, marginTop: 6 }}
                      onClick={() => void copyText(installCommands.manualCmd)}
                    >
                      Copy commands
                    </button>
                  </div>
                )}
                {installTab === "docker" && (
                  <div style={{ opacity: 0.5, pointerEvents: "none" }}>
                    <div style={{ ...codeBlock, color: "#94A3B8" }}>{installCommands.dockerCmd}</div>
                    <p style={{ fontSize: 12, color: "#64748B", marginTop: 8 }}>Docker image coming soon.</p>
                  </div>
                )}

                <div style={{ marginTop: 16 }}>
                  <p style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 700, color: "#334155" }}>
                    What are you running?
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {SCHEDULERS.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setScheduler(s)}
                        style={{
                          padding: "8px 14px",
                          borderRadius: 999,
                          border: scheduler === s ? "2px solid #0EA5A0" : "1px solid #CBD5E1",
                          background: scheduler === s ? "#CCFBF1" : "#FFF",
                          fontWeight: 600,
                          fontSize: 13,
                          cursor: "pointer",
                          textTransform: "capitalize",
                        }}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  type="button"
                  style={{ ...primaryBtn, marginTop: 16 }}
                  disabled={!agentToken}
                  onClick={() => goToStep(4)}
                >
                  I&apos;ve started the agent →
                </button>
                <button type="button" style={secondaryBtn} onClick={() => goToStep(2)}>
                  ← Back
                </button>
              </>
            )}

            {step === 4 && (
              <>
                <h2 style={{ margin: "0 0 16px", fontSize: 22, fontWeight: 800 }}>Waiting for your agent…</h2>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
                  <div
                    style={{
                      width: 88,
                      height: 88,
                      borderRadius: "50%",
                      background:
                        verifyPhase === "connected"
                          ? "radial-gradient(circle, #86EFAC 0%, #22C55E 70%)"
                          : verifyPhase === "detected"
                          ? "radial-gradient(circle, #FDE68A 0%, #FACC15 70%)"
                          : "radial-gradient(circle, #E2E8F0 0%, #94A3B8 70%)",
                      animation: verifyPhase === "connected" ? "none" : "pulse 1.8s ease-in-out infinite",
                      boxShadow:
                        verifyPhase === "connected"
                          ? "0 0 0 12px rgba(34, 197, 94, 0.2)"
                          : "0 0 0 8px rgba(148, 163, 184, 0.15)",
                    }}
                  />
                  <p
                    style={{
                      margin: 0,
                      fontSize: 16,
                      fontWeight: 700,
                      color:
                        verifyPhase === "connected"
                          ? "#15803D"
                          : verifyPhase === "detected"
                          ? "#A16207"
                          : "#64748B",
                    }}
                  >
                    {verifyPhase === "connected" && "Agent connected ✓"}
                    {verifyPhase === "detected" && "Agent detected — verifying…"}
                    {verifyPhase === "waiting" && "Waiting for agent…"}
                  </p>
                  {currentPriceMwh != null && (
                    <p style={{ margin: 0, fontSize: 13, color: "#64748B" }}>
                      Grid: {marketRegion} · ${currentPriceMwh.toFixed(2)}/MWh
                    </p>
                  )}
                </div>

                {verifyTimedOut && verifyPhase !== "connected" && (
                  <div
                    style={{
                      marginTop: 16,
                      padding: 14,
                      borderRadius: 10,
                      background: "#F8FAFC",
                      border: "1px solid #E2E8F0",
                    }}
                  >
                    <p style={{ margin: "0 0 10px", fontSize: 14, fontWeight: 600, color: "#334155" }}>
                      Taking longer than expected? Here&apos;s how to check:
                    </p>
                    <div style={codeBlock}>eloplus status{"\n"}eloplus logs</div>
                    <button
                      type="button"
                      style={{ ...secondaryBtn, marginTop: 12, fontWeight: 700, color: "#0EA5A0" }}
                      onClick={() => {
                        writeOnboardingComplete();
                        onComplete();
                      }}
                    >
                      Skip for now
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {error ? (
            <p style={{ margin: 0, fontSize: 13, color: "#DC2626", fontWeight: 600 }}>{error}</p>
          ) : null}
        </div>
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.06); opacity: 0.85; }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}
